/* eslint-disable unicorn/prefer-module */
import { useCallback, useEffect, useMemo, useState } from "react";
import { ImCheckboxChecked, ImCheckboxUnchecked } from "react-icons/im";
import cx from "classix";
import { Loader } from "..";
import { getEdificiosOperativosPublico, useCategoriaMoneda } from "../../utils";
import { Header } from "../Header";
import styles from "./CategoriaBelleza.module.scss";

// =====================================================
// DATOS IPSOS BELLEZA Y CUIDADO PERSONAL
// Fuente: IPSOS Wecast 2025 · Base 400 (penetración) / 261 usuarios (resto)
//
// NOTA DE DATOS: los rótulos "Tiendas por departamento", "Tiendas especializadas"
// y "Plataformas online" (en dondeCompra) fueron definidos por Ignacio — venían
// truncados en la fuente del estudio. El resto de los valores es IPSOS textual.
// En marcas se usa la grafía real corregida (NO la del estudio) + el holding
// propietario de cada marca (holding NO se muestra en ningún lado; queda en la
// data para eventual agrupación futura).
// =====================================================
const IPSOS_BELLEZA = {
  // Penetración — base 400
  penetracion: { usa: 0.659, noUsa: 0.341 },

  // Tipo de producto que compra — base 261 (usuarios de belleza)
  tipoProducto: {
    facial: { pct: 0.795, label: "Facial" },
    perfumes: { pct: 0.592, label: "Perfumes" },
    higiene: { pct: 0.538, label: "Higiene" },
    capilar: { pct: 0.521, label: "Capilar" },
    corporal: { pct: 0.466, label: "Corporal" },
    maquillaje: { pct: 0.325, label: "Maquillaje" },
    manicura: { pct: 0.169, label: "Manicura" },
  },

  // Frecuencia de compra — base 261 (ordinal). factor = tickets/mes por persona
  frecuencia: [
    { pct: 0.141, label: "Semanal", factor: 4 },
    { pct: 0.694, label: "Mensual", factor: 1 },
    { pct: 0.145, label: "Cada 2 a 3 meses", factor: 0.4 },
    { pct: 0.02, label: "Cada 6 meses o más", factor: 0.17 },
  ],

  // Gasto mensual — base 261 (ordinal, tramos). midpoint = punto medio del tramo
  gastoMensual: [
    { pct: 0.043, label: "<$10.000", midpoint: 5000 },
    { pct: 0.335, label: "$10.000-$30.000", midpoint: 20000 },
    { pct: 0.393, label: "$30.001-$50.000", midpoint: 40000 },
    { pct: 0.229, label: ">$50.000", midpoint: 75000 },
  ],

  // Dónde compra — base 261
  dondeCompra: [
    { pct: 0.618, label: "Farmacias" },
    { pct: 0.3, label: "Tiendas por departamento" },
    { pct: 0.227, label: "Tiendas especializadas" },
    { pct: 0.205, label: "Supermercados" },
    { pct: 0.114, label: "Plataformas online" },
    { pct: 0.05, label: "Redes sociales" },
    { pct: 0.043, label: "Mercado Pago" },
  ],

  // Factores de decisión — base 261
  factores: [
    { pct: 0.902, label: "Calidad" },
    { pct: 0.462, label: "Precio" },
    { pct: 0.152, label: "Ingredientes naturales o veganos" },
    { pct: 0.098, label: "Marca reconocida" },
    { pct: 0.036, label: "Recomendaciones (amigos, influencers, etc.)" },
    { pct: 0.032, label: "Cruelty-free" },
    { pct: 0.014, label: "Sostenibilidad (envases reciclables, eco-friendly)" },
  ],

  // Importancia de la categoría — base 261
  importancia: [
    { pct: 0.771, label: "Muy importante" },
    { pct: 0.143, label: "Bastante importante" },
    { pct: 0.087, label: "Algo importante" },
  ],

  // Marcas — mención espontánea — base 261 (grafía real + holding)
  marcasEspontanea: [
    { nombre: "Nivea", pct: 0.344, holding: "Beiersdorf" },
    { nombre: "Maybelline", pct: 0.287, holding: "L'Oréal" },
    { nombre: "Dove", pct: 0.203, holding: "Unilever" },
    { nombre: "Garnier", pct: 0.12, holding: "L'Oréal" },
    { nombre: "Rexona", pct: 0.099, holding: "Unilever" },
    { nombre: "Clinique", pct: 0.087, holding: "Estée Lauder" },
    { nombre: "Gillette", pct: 0.077, holding: "P&G" },
    { nombre: "La Roche-Posay", pct: 0.073, holding: "L'Oréal" },
    { nombre: "Neutrogena", pct: 0.043, holding: "Kenvue" },
    { nombre: "L'Oréal", pct: 0.03, holding: "L'Oréal" },
    { nombre: "Eucerin", pct: 0.03, holding: "Beiersdorf" },
    { nombre: "Isdin", pct: 0.023, holding: "Isdin" },
    { nombre: "Mennen", pct: 0.019, holding: "Colgate-Palmolive" },
    { nombre: "Elvive", pct: 0.018, holding: "L'Oréal" },
    { nombre: "Vichy", pct: 0.017, holding: "L'Oréal" },
    { nombre: "Lancôme", pct: 0.016, holding: "L'Oréal" },
    { nombre: "Pantene", pct: 0.008, holding: "P&G" },
    { nombre: "Marca coreana", pct: 0.006, holding: "—" },
  ],

  // Marcas — mención inducida — base 261 (grafía real + holding)
  marcasGuiada: [
    { nombre: "Maybelline", pct: 0.206, holding: "L'Oréal" },
    { nombre: "Dove", pct: 0.197, holding: "Unilever" },
    { nombre: "Nivea", pct: 0.195, holding: "Beiersdorf" },
    { nombre: "Gillette", pct: 0.153, holding: "P&G" },
    { nombre: "Clinique", pct: 0.126, holding: "Estée Lauder" },
    { nombre: "L'Oréal", pct: 0.105, holding: "L'Oréal" },
    { nombre: "Elvive", pct: 0.091, holding: "L'Oréal" },
    { nombre: "Garnier", pct: 0.078, holding: "L'Oréal" },
    { nombre: "Rexona", pct: 0.071, holding: "Unilever" },
    { nombre: "Otra", pct: 0.079, holding: "—" },
  ],
};

// Promedio ponderado de gasto (midpoints × share), redondeado a la centena → $39.800
const PROMEDIO_PONDERADO_GASTO =
  Math.round(IPSOS_BELLEZA.gastoMensual.reduce((s, g) => s + g.pct * g.midpoint, 0) / 100) * 100;

// Checkpoints de monto (gasto mensual por persona)
const MONTOS = [
  { valor: 10000, promedio: false },
  { valor: 20000, promedio: false },
  { valor: 40000, promedio: false },
  { valor: 75000, promedio: false },
  { valor: PROMEDIO_PONDERADO_GASTO, promedio: true },
];

// Marcas ordenadas por share desc (para el grid de market share)
const MARCAS_ESPONTANEA = [...IPSOS_BELLEZA.marcasEspontanea].sort((a, b) => b.pct - a.pct);
const MARCAS_INDUCIDA = [...IPSOS_BELLEZA.marcasGuiada].sort((a, b) => b.pct - a.pct);

const pct0 = (pct) => `${(pct * 100).toFixed(0)}%`;

export const Belleza = () => {
  const { formatNumber, formatLocal, convertCLP, pais } = useCategoriaMoneda();
  const [edificios, setEdificios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkAll, setCheckAll] = useState(true);
  const [checkedBuildings, setCheckedBuildings] = useState({});
  const [showInventarioModal, setShowInventarioModal] = useState(false);
  const [verAnual, setVerAnual] = useState(false);
  const [montoSeleccionado, setMontoSeleccionado] = useState(PROMEDIO_PONDERADO_GASTO);

  useEffect(() => {
    const initialize = async () => {
      try {
        const newEdificios = await getEdificiosOperativosPublico({ pais });
        setEdificios(newEdificios);

        const initialChecked = {};
        newEdificios.forEach(({ id }) => {
          initialChecked[id] = true;
        });
        setCheckedBuildings(initialChecked);
      } finally {
        setLoading(false);
      }
    };

    initialize();
  }, [pais]);

  const toggleCheckAll = useCallback(() => {
    if (!checkAll) {
      const allChecked = {};
      edificios.forEach(({ id }) => {
        allChecked[id] = true;
      });
      setCheckedBuildings(allChecked);
    } else {
      setCheckedBuildings({});
    }
    setCheckAll((prev) => !prev);
  }, [checkAll, edificios]);

  const toggleCheck = useCallback((id) => {
    setCheckedBuildings((prev) => ({ ...prev, [id]: !prev[id] }));
  }, []);

  const { totalEjecutivos, cantidadEdificios, totalPantallas, totalEmpresas } = useMemo(() => {
    let ejecutivos = 0;
    let cantidad = 0;
    let pantallas = 0;
    let empresas = 0;

    edificios.forEach(({ id, personas, pantallas: p, espera, totems, cowork, empresaslistado }) => {
      if (checkedBuildings[id]) {
        cantidad++;
        ejecutivos += personas || 0;
        pantallas += (p || 0) + (espera || 0) + (totems || 0) + (cowork || 0);
        empresas += empresaslistado ? empresaslistado.split(",").length : 0;
      }
    });

    return {
      totalEjecutivos: ejecutivos,
      cantidadEdificios: cantidad,
      totalPantallas: pantallas,
      totalEmpresas: empresas,
    };
  }, [edificios, checkedBuildings]);

  // =====================================================
  // CÁLCULOS PRINCIPALES — recalcula con el inventario y el monto
  // =====================================================
  const resultados = useMemo(() => {
    if (!totalEjecutivos) return null;

    const compradores = Math.round(totalEjecutivos * IPSOS_BELLEZA.penetracion.usa);
    const noUsan = Math.round(totalEjecutivos * IPSOS_BELLEZA.penetracion.noUsa);

    const desgloseFrecuencia = IPSOS_BELLEZA.frecuencia.map((f) => {
      const personas = Math.round(compradores * f.pct);
      const tickets = Math.round(personas * f.factor);
      return { ...f, personas, tickets };
    });
    const ticketsMes = desgloseFrecuencia.reduce((s, d) => s + d.tickets, 0);
    const mercadoMensual = compradores * montoSeleccionado;

    const marketShare = (marcas) =>
      marcas.map((m) => ({
        nombre: m.nombre,
        share: m.pct,
        clientes: Math.round(compradores * m.pct),
        mercadoMensual: Math.round(compradores * m.pct * montoSeleccionado),
      }));

    return {
      compradores,
      noUsan,
      desgloseFrecuencia,
      ticketsMes,
      ticketsAnio: ticketsMes * 12,
      mercadoMensual,
      mercadoAnual: mercadoMensual * 12,
      marketEspontanea: marketShare(MARCAS_ESPONTANEA),
      marketInducida: marketShare(MARCAS_INDUCIDA),
    };
  }, [totalEjecutivos, montoSeleccionado]);

  if (loading || edificios.length === 0) return <Loader />;

  const tipoProductoEntries = Object.values(IPSOS_BELLEZA.tipoProducto);

  // Ranking de barras horizontales (tipo de producto / dónde compra)
  const renderBarras = (titulo, entries) => (
    <div className={styles.appsCard}>
      <h4 className={styles.subtitulo}>{titulo}</h4>
      <div className={styles.appsList}>
        {entries.map(({ label, pct }) => (
          <div key={label} className={styles.appItem}>
            <div className={styles.appNombre}>{label}</div>
            <div className={styles.appBar}>
              <div className={styles.appBarFill} style={{ width: `${pct * 100}%` }} />
            </div>
            <div className={styles.appStats}>
              <span className={styles.badgeIpsos}>{pct0(pct)}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  // Grid de cards de market share por marca
  const renderMarketShare = (titulo, data) => (
    <div className={styles.marketShareContainer}>
      <h4 className={styles.marketShareTitulo}>{titulo}</h4>
      <div className={styles.marketShareGrid}>
        {data.map((m) => (
          <div key={m.nombre} className={styles.marketShareCard}>
            <div className={styles.cardNombre}>{m.nombre}</div>
            <div className={styles.cardShare}>
              <span className={styles.badgeIpsos}>{pct0(m.share)}</span>
            </div>
            <div className={styles.cardMetricas}>
              <div>
                <span className={styles.metricaLabel}>Clientes</span>
                <span className={styles.metricaValor}>{formatNumber(m.clientes)}</span>
              </div>
              <div>
                <span className={styles.metricaLabel}>Mercado/{verAnual ? "año" : "mes"}</span>
                <span className={styles.metricaValor}>
                  {formatLocal(convertCLP(verAnual ? m.mercadoMensual * 12 : m.mercadoMensual))}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className={styles.bellezaPage}>
      <Header leftContent={<h1 className={styles.headerTitulo}>BELLEZA Y CUIDADO PERSONAL</h1>}>
        <div className={styles.inventarioHeader}>
          <div className={styles.inventarioInfo}>
            <span>
              <strong>{cantidadEdificios}</strong> edificios
            </span>
            <span className={styles.separadorVertical}>|</span>
            <span>
              <strong>{formatNumber(totalPantallas)}</strong> pantallas
            </span>
            <span className={styles.separadorVertical}>|</span>
            <span>
              <strong>{formatNumber(totalEjecutivos)}</strong> personas
            </span>
            <span className={styles.separadorVertical}>|</span>
            <span>
              <strong>{formatNumber(totalEmpresas)}</strong> empresas
            </span>
          </div>
          <button type="button" className={styles.inventarioBtn} onClick={() => setShowInventarioModal(true)}>
            Inventario
          </button>
        </div>
      </Header>

      <div className={styles.bellezaContent}>
        {/* Tabla maestra: penetración + frecuencia + tickets */}
        {resultados && (
          <div className={styles.tablaContainer}>
            <table className={cx("table table-sm", styles.tablaResultados)}>
              <thead className="table-dark">
                <tr>
                  <th className={cx("text-center", styles.thNoCompran)}>No usan</th>
                  <th className={cx("text-center", styles.thSiCompran)}>Sí usan</th>
                  <th className={cx("text-center", styles.thFrecuencia)}>Frecuencia de compra</th>
                  <th className={cx("text-center", styles.thPersonas)}>Según frecuencia</th>
                  <th className={cx("text-center", styles.thConversiones)}>Tickets {verAnual ? "año" : "mes"}</th>
                </tr>
              </thead>
              <tbody>
                {resultados.desgloseFrecuencia.map((data, idx) => (
                  <tr key={data.label}>
                    {idx === 0 && (
                      <>
                        <td
                          rowSpan={resultados.desgloseFrecuencia.length}
                          className={cx("text-center align-middle", styles.celdaNo)}
                        >
                          <div className={styles.celdaContenido}>
                            <span className={cx(styles.badgeIpsos, styles.badgeOportunidad)}>
                              {pct0(IPSOS_BELLEZA.penetracion.noUsa)}
                            </span>
                            <div className={styles.valorMuyGrande}>{formatNumber(resultados.noUsan)}</div>
                            <div className={styles.subtextoOportunidad}>OPORTUNIDAD</div>
                          </div>
                        </td>
                        <td
                          rowSpan={resultados.desgloseFrecuencia.length}
                          className={cx("text-center align-middle", styles.celdaSi)}
                        >
                          <div className={styles.celdaContenido}>
                            <span className={styles.badgeIpsos}>{pct0(IPSOS_BELLEZA.penetracion.usa)}</span>
                            <div className={styles.valorMuyGrande}>{formatNumber(resultados.compradores)}</div>
                          </div>
                        </td>
                      </>
                    )}
                    <td className={styles.celdaFrecuencia}>
                      <div className={styles.frecuenciaInner}>
                        <span className={styles.badgeIpsos}>{pct0(data.pct)}</span>
                        <span>{data.label}</span>
                      </div>
                    </td>
                    <td className="text-center">{formatNumber(data.personas)}</td>
                    <td className="text-center">{formatNumber(verAnual ? data.tickets * 12 : data.tickets)}</td>
                  </tr>
                ))}
                <tr className={styles.filaTotales}>
                  <td colSpan={4} className={styles.celdaVacia} />
                  <td className={cx("text-center", styles.celdaTotal)}>
                    <strong>{formatNumber(verAnual ? resultados.ticketsAnio : resultados.ticketsMes)}</strong>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {/* Gasto (radios) + Mercado (toggle) */}
        {resultados && (
          <div className={styles.dosBloquesContainer}>
            <div className={styles.bloqueTicket}>
              <div className={styles.ticketLabel}>Gasto mensual por persona</div>
              <div className={styles.ticketBox}>
                <div className={styles.ticketOpciones}>
                  {MONTOS.map((m) => (
                    <label key={m.promedio ? "promedio" : m.valor} className={styles.ticketRadio}>
                      <input
                        type="radio"
                        name="montoBelleza"
                        checked={montoSeleccionado === m.valor}
                        onChange={() => setMontoSeleccionado(m.valor)}
                      />
                      <span>
                        {m.promedio
                          ? `Promedio (${formatLocal(convertCLP(m.valor))})`
                          : formatLocal(convertCLP(m.valor))}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            <div className={styles.bloqueMercado}>
              <div className={styles.mercadoLabelRow}>
                <span className={styles.mercadoLabel}>Mercado {verAnual ? "anual" : "mensual"}</span>
                <div className={styles.toggleContainer}>
                  <span className={cx(styles.toggleLabel, !verAnual && styles.toggleActive)}>mes</span>
                  <label className={styles.toggleSwitch}>
                    <input type="checkbox" checked={verAnual} onChange={(e) => setVerAnual(e.target.checked)} />
                    <span className={styles.toggleSlider} />
                  </label>
                  <span className={cx(styles.toggleLabel, verAnual && styles.toggleActive)}>año</span>
                </div>
              </div>
              <div className={styles.mercadoValorGrande}>
                {formatLocal(convertCLP(verAnual ? resultados.mercadoAnual : resultados.mercadoMensual))}
              </div>
            </div>
          </div>
        )}

        {/* Market Share por Marca — espontánea + inducida */}
        {resultados && renderMarketShare("Market Share por Marca — mención espontánea", resultados.marketEspontanea)}
        {resultados && renderMarketShare("Market Share por Marca — mención inducida", resultados.marketInducida)}

        {/* Cómo se compra — barras */}
        <div className={styles.appsSection}>
          {renderBarras("Tipo de producto que compra", tipoProductoEntries)}
          {renderBarras("Dónde compra", IPSOS_BELLEZA.dondeCompra)}
        </div>

        {/* Factores + Importancia — chips */}
        <div className={styles.valoracionContainer}>
          <h4 className={styles.subtitulo}>Factores que prioriza</h4>
          <div className={styles.valoracionGrid}>
            {IPSOS_BELLEZA.factores.map(({ label, pct }) => (
              <div key={label} className={styles.valoracionItem}>
                <span className={styles.valoracionNombre}>{label}</span>
                <span className={styles.badgeIpsos}>{pct0(pct)}</span>
              </div>
            ))}
          </div>
        </div>

        <div className={styles.valoracionContainer}>
          <h4 className={styles.subtitulo}>Importancia de la categoría</h4>
          <div className={styles.valoracionGrid}>
            {IPSOS_BELLEZA.importancia.map(({ label, pct }) => (
              <div key={label} className={styles.valoracionItem}>
                <span className={styles.valoracionNombre}>{label}</span>
                <span className={styles.badgeIpsos}>{pct0(pct)}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Disclaimer */}
        <div className={styles.disclaimer}>
          <p>
            <strong>Fuente:</strong> IPSOS Wecast 2025. Base: 400 casos (penetración) / 261 usuarios (resto). El 66% usa
            productos de belleza y cuidado personal; el 77% considera la categoría muy importante y la calidad (90%) es
            el principal factor de decisión. Los rótulos de canales &quot;Tiendas por departamento&quot;, &quot;Tiendas
            especializadas&quot; y &quot;Plataformas online&quot; fueron definidos internamente (venían truncados en la
            fuente). El mercado se calcula sobre la audiencia seleccionada en el inventario.
          </p>
        </div>
      </div>

      {/* Modal de Inventario */}
      {showInventarioModal && (
        <div className={styles.modalOverlay} onClick={() => setShowInventarioModal(false)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>Inventario de Edificios</h3>
              <button type="button" className={styles.modalClose} onClick={() => setShowInventarioModal(false)}>
                ×
              </button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.modalSummary}>
                <span>
                  <strong>{cantidadEdificios}</strong> edificios seleccionados
                </span>
                <span>
                  <strong>{formatNumber(totalEjecutivos)}</strong> personas
                </span>
                <button type="button" className={styles.toggleAllBtn} onClick={toggleCheckAll}>
                  {checkAll ? "Deseleccionar todos" : "Seleccionar todos"}
                </button>
              </div>
              <table className="table table-sm table-hover">
                <thead className="table-dark">
                  <tr>
                    <th className="text-center">✓</th>
                    <th className="text-center">Edificio</th>
                    <th className="text-center">Ubicación</th>
                    <th className="text-center">Pantallas</th>
                    <th className="text-center">Personas</th>
                  </tr>
                </thead>
                <tbody>
                  {edificios.map(({ id, edificio, ubicacion, personas, pantallas, espera, totems, cowork }) => (
                    <tr
                      key={id}
                      onClick={() => toggleCheck(id)}
                      className={cx(checkedBuildings[id] && "table-success")}
                      style={{ cursor: "pointer" }}
                    >
                      <td className="text-center">
                        {checkedBuildings[id] ? <ImCheckboxChecked size={16} /> : <ImCheckboxUnchecked size={16} />}
                      </td>
                      <td className="text-center">{edificio.toUpperCase()}</td>
                      <td className="text-center">{ubicacion}</td>
                      <td className="text-center">
                        {formatNumber((pantallas || 0) + (espera || 0) + (totems || 0) + (cowork || 0))}
                      </td>
                      <td className="text-center">{personas ? formatNumber(personas) : "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className={styles.modalFooter}>
              <button type="button" className={styles.modalBtn} onClick={() => setShowInventarioModal(false)}>
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Belleza;
